import React from 'react';
import {Box, Chip, Typography} from "@mui/joy";
import {useTranslation} from "react-i18next";
import {SubdirectoryArrowRightRounded} from "@mui-symbols-material/w300";
import {DepartmentIcon, ServiceProviderIcon} from "@dreebit-registry/vsm-microservice-components";
import {getServiceUnitSourceInfo} from "../../utils/serviceUnitSource";
import ContactChannelLinks from "../ContactChannelLinks";
import ContactNameRow from "../ContactNameRow";

// Named contacts go side by side once the surrounding box can give each column
// enough room for a full email address (~200px) — below that they stack, so the
// narrow dashboard card never ellipsises every address.
const TWO_COLUMN_MIN_WIDTH = 410;
const TWO_COLUMN_QUERY = `@container (min-width: ${TWO_COLUMN_MIN_WIDTH}px)`;

export interface ServiceUnitContactInfo {
    id: string;
    name: string;
    email?: string | null;
    phone?: string | null;
    // single free-text role, localized on read; null when none was given
    role?: string | null;
}

export interface ResolvedServiceUnitInfo {
    source?: string | null;
    // set only when source is ANCESTOR_NODE
    inheritedFrom?: {
        id: string;
        title?: string | null;
        pathNodes?: ({ id: string } | null)[] | null;
    } | null;
    unit?: {
        id: string;
        type?: string | null;
        name: string;
        // unit-level hotline, not a person's contact data
        email?: string | null;
        phone?: string | null;
        // named people the unit publishes — frequently and legitimately empty,
        // in which case the hotline is the whole contact information
        serviceContacts?: (ServiceUnitContactInfo | null)[] | null;
    } | null;
}

interface Props {
    resolved: ResolvedServiceUnitInfo;
    // makes the ANCESTOR_NODE origin chip navigate the tree to that ancestor
    onNavigateToOrigin?: (inheritedFrom: NonNullable<ResolvedServiceUnitInfo['inheritedFrom']>) => void;
    // caps the named contacts (fixed-height widget context); the rest is
    // summarised as "+ N more contacts"
    maxContacts?: number;
    // 'customer' (default): the viewer is served by this unit — "your contact
    // persons", "assigned to your company". 'provider': the viewer belongs to
    // the service organisation and reads the answer for someone else's element.
    perspective?: 'customer' | 'provider';
    // false when the surrounding widget already shows the type icon in its title
    showTypeIcon?: boolean;
    // 'compact' (default): hero/widget typography — bodySmall throughout, the
    // named contacts as a tight list that fits the fixed-height dashboard
    // widget. 'detail': the node contact tab — the contact cards' look
    // (bodyBold name, role chip, body-sm links), so the box matches the contact
    // persons listed below it.
    variant?: 'compact' | 'detail';
}

/**
 * Body of every "who serves this customer/node" widget (IBM-972): unit name
 * with type icon, a source chip explaining where the answer came from, the
 * unit hotline and — where the unit publishes any — its named contacts.
 * Typography follows the hero cards (HeroCustomerCard): titleSmall for the
 * entity, bodySmall for everything secondary; the `detail` variant switches the
 * hotline and named contacts to the contact-card typography instead. Fallback
 * sources get a distinct, explained presentation so a shared default is never
 * mistaken for a dedicated assignment.
 */
const ServiceUnitContent = ({
    resolved,
    onNavigateToOrigin,
    maxContacts,
    perspective = 'customer',
    showTypeIcon = true,
    variant = 'compact',
}: Props) => {
    const {t} = useTranslation();
    const unit = resolved.unit;
    const inheritedFrom = resolved.inheritedFrom;
    const sourceInfo = getServiceUnitSourceInfo(resolved.source, t, {
        ancestorTitle: inheritedFrom?.title,
        unitType: resolved.unit?.type,
        perspective,
    });

    if (!unit) return null;

    const serviceContacts = (unit.serviceContacts ?? []).filter(Boolean) as ServiceUnitContactInfo[];
    const visibleContacts = maxContacts ? serviceContacts.slice(0, maxContacts) : serviceContacts;
    const hiddenContactCount = serviceContacts.length - visibleContacts.length;
    const isAncestorOrigin = resolved.source === 'ANCESTOR_NODE' && !!inheritedFrom && !!onNavigateToOrigin;
    const TypeIcon = unit.type === 'DEPARTMENT' ? DepartmentIcon : ServiceProviderIcon;
    const isDetail = variant === 'detail';
    const linkLevel = isDetail ? 'body-sm' : 'bodySmall';
    const caption = perspective === 'provider' ? t('Named contacts') : t('Your contact persons');

    const renderCompactContact = (contact: ServiceUnitContactInfo) => (
        <Box key={contact.id} sx={{minWidth: 0}} data-testid="service-unit-contact">
            <Box sx={{display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0}}>
                <Typography level="bodySmall" sx={{fontWeight: 600, color: '#333'}} noWrap>
                    {contact.name}
                </Typography>
                {contact.role && (
                    <Typography level="bodySmall" sx={{color: '#777'}} noWrap>
                        {contact.role}
                    </Typography>
                )}
            </Box>
            <ContactChannelLinks email={contact.email} phone={contact.phone} level="bodySmall"/>
        </Box>
    );

    // Same anatomy as ContactPersonCard, minus the card border: the box around
    // the whole service unit already frames these.
    const renderDetailContact = (contact: ServiceUnitContactInfo) => (
        <Box key={contact.id} sx={{minWidth: 0}} data-testid="service-unit-contact">
            <ContactNameRow name={contact.name} role={contact.role} roleTestId="service-unit-contact-role"/>
            <ContactChannelLinks email={contact.email} phone={contact.phone} level="body-sm" sx={{mt: '6px'}}/>
        </Box>
    );

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', minWidth: 0}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: '12px', mt: '12px', minWidth: 0}}>
                {showTypeIcon && <TypeIcon size={42} color="#333"/>}
                <Box sx={{flex: 1, minWidth: 0}}>
                    {/* explicit colors — the dashboard widget context inherits white */}
                    <Typography level="titleSmall" noWrap sx={{color: '#333'}} data-testid="service-unit-name">
                        {unit.name}
                    </Typography>
                    {sourceInfo.label && (
                        <Chip
                            size="sm"
                            color="neutral"
                            variant={sourceInfo.isFallback ? 'soft' : 'outlined'}
                            data-testid="service-unit-source"
                            startDecorator={isAncestorOrigin ? <SubdirectoryArrowRightRounded sx={{fontSize: 14}}/> : undefined}
                            onClick={isAncestorOrigin ? () => onNavigateToOrigin!(inheritedFrom!) : undefined}
                            sx={{mt: '2px', ...(isAncestorOrigin ? {cursor: 'pointer'} : {})}}
                        >
                            {sourceInfo.label}
                        </Chip>
                    )}
                </Box>
            </Box>

            {sourceInfo.isFallback && sourceInfo.caption && (
                <Typography level="bodySmall" sx={{color: '#777', mt: '8px'}} data-testid="service-unit-fallback-caption">
                    {sourceInfo.caption}
                </Typography>
            )}

            {(unit.email || unit.phone) && (
                <ContactChannelLinks
                    email={unit.email}
                    phone={unit.phone}
                    level={linkLevel}
                    sx={{mt: '10px'}}
                    data-testid="service-unit-hotline"
                />
            )}

            {serviceContacts.length > 0 && (
                <Box sx={{mt: '14px', pt: '12px', borderTop: '1px solid #eee', containerType: 'inline-size'}}>
                    {isDetail ? (
                        <>
                            {/* caption styled like the "Directly assigned" section titles below */}
                            <Typography level="bodyBold" sx={{color: '#666666', mb: '10px'}}>
                                {caption}
                            </Typography>
                            {/* CSS grid instead of Joy Grid — same as InstalledBaseNodeContacts: its
                                negative spacing margins overflow the tab's scroll container.
                                Two columns are a question of the BOX's width, not the viewport's —
                                the same content renders in a wide contact tab and in a narrow
                                dashboard column, so this is a container query. */}
                            <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: '1fr',
                                gap: '12px',
                                [TWO_COLUMN_QUERY]: {gridTemplateColumns: 'repeat(2, minmax(0, 1fr))'},
                            }}>
                                {visibleContacts.map(renderDetailContact)}
                            </Box>
                        </>
                    ) : (
                        <Box sx={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
                            <Typography level="bodySmall" sx={{color: '#777'}}>
                                {caption}
                            </Typography>
                            {visibleContacts.map(renderCompactContact)}
                        </Box>
                    )}
                    {hiddenContactCount > 0 && (
                        <Typography level="bodySmall" sx={{color: '#777', mt: '10px'}}>
                            {t('+ {{count}} more contacts', {count: hiddenContactCount})}
                        </Typography>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default ServiceUnitContent;
